import { MENS_APPARATUS, WOMENS_APPARATUS } from '../constants/competitionConstants';

/**
 * Parse a datetime string into a Date object
 * Handles formats like "2024-03-15T09:00:00-07:00" and "2025-01-04 09:00:00"
 */
function parseDateTime(dateTimeStr) {
  if (!dateTimeStr) return null;
  const date = new Date(dateTimeStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Calculate time-based progress for a session
 * @returns {number} Progress percentage (0-100) based on current time within session window
 */
function getSessionTimeProgress(session) {
  const startTime = parseDateTime(
    session.timeBlocks?.competitionStart || session.startDate
  );
  const endTime = parseDateTime(session.timeBlocks?.competitionEnd);

  if (!startTime) return null; // Can't calculate without start time

  const now = new Date();

  // Session hasn't started
  if (now < startTime) return 0;

  // No end time defined, can't calculate time progress
  if (!endTime) return null;

  // Session has ended
  if (now >= endTime) return 100;

  // Session in progress - calculate percentage through
  const totalDuration = endTime.getTime() - startTime.getTime();
  const elapsed = now.getTime() - startTime.getTime();

  return Math.round((elapsed / totalDuration) * 100);
}

/**
 * Calculate competition progress percentage based on scores entered and session times.
 * Uses a hybrid approach:
 * - For sessions not started: 0%
 * - For sessions in progress: max(score %, time %) for smoother progress
 * - For sessions ended: score %
 *
 * @param {Array} sessions - Array of session objects with sessionNumber, timeBlocks
 * @param {Array} gymnasts - Array of gymnast objects with scores, session, and scratched fields
 * @param {string} gender - 'M' for men, 'F' for women (determines apparatus count)
 * @returns {Object} Progress information
 */
export function calculateCompetitionProgress(sessions, gymnasts, gender = 'F') {
  if (!sessions?.length) {
    return {
      percentage: 0,
      scoresEntered: 0,
      expectedScores: 0,
      sessionProgress: [],
      status: 'not_started',
      completedSessions: 0,
      totalSessions: 0
    };
  }

  const apparatus = gender === 'M' ? MENS_APPARATUS : WOMENS_APPARATUS;
  const apparatusCount = apparatus.length;

  // Calculate progress for each session
  const sessionProgress = sessions.map(session => {
    const sessionNumber = session.sessionNumber;

    // Get active (non-scratched) gymnasts in this session
    const sessionGymnasts = (gymnasts || []).filter(
      g => Number(g.session) === Number(sessionNumber) && !g.scratched
    );

    const athleteCount = sessionGymnasts.length;
    const expectedScores = athleteCount * apparatusCount;

    // Count scores entered (score.final > 0)
    let scoresEntered = 0;
    sessionGymnasts.forEach(athlete => {
      for (let i = 1; i <= apparatusCount; i++) {
        const eventKey = `E${i}`;
        const score = athlete.scores?.[eventKey]?.final;
        if (score && score > 0) {
          scoresEntered++;
        }
      }
    });

    const scorePercentage = expectedScores > 0
      ? Math.round((scoresEntered / expectedScores) * 100)
      : 0;

    // Get time-based progress
    const timeProgress = getSessionTimeProgress(session);

    // Determine effective percentage:
    // - Use score % if we have scores
    // - Use time % for smoother progress during session if no scores yet
    // - Use max of both for best UX (progress never goes backward)
    let effectivePercentage;
    if (timeProgress === null) {
      // No time data available, use score-based only
      effectivePercentage = scorePercentage;
    } else if (timeProgress === 100) {
      // Session time has ended, use score percentage
      effectivePercentage = scorePercentage;
    } else {
      // Session in progress or not started, use the higher of time or score
      effectivePercentage = Math.max(scorePercentage, timeProgress);
    }

    return {
      sessionNumber,
      athleteCount,
      expectedScores,
      scoresEntered,
      scorePercentage,
      timeProgress,
      percentage: effectivePercentage,
      isComplete: scoresEntered >= expectedScores && expectedScores > 0
    };
  });

  // Calculate overall progress as average of session percentages
  // This gives equal weight to each session regardless of athlete count
  const totalPercentage = sessionProgress.reduce((sum, s) => sum + s.percentage, 0);
  const percentage = sessions.length > 0
    ? Math.round(totalPercentage / sessions.length)
    : 0;

  const completedSessions = sessionProgress.filter(s => s.isComplete).length;
  const totalExpected = sessionProgress.reduce((sum, s) => sum + s.expectedScores, 0);
  const totalEntered = sessionProgress.reduce((sum, s) => sum + s.scoresEntered, 0);

  // Determine status
  let status = 'not_started';
  if (completedSessions === sessions.length && sessions.length > 0) {
    status = 'complete';
  } else if (percentage > 0 || totalEntered > 0) {
    status = 'in_progress';
  }

  return {
    percentage,
    scoresEntered: totalEntered,
    expectedScores: totalExpected,
    completedSessions,
    totalSessions: sessions.length,
    sessionProgress,
    status
  };
}

/**
 * Get a human-readable progress label
 * @param {Object} progress - Progress object from calculateCompetitionProgress
 * @returns {string} Label like "2/4 Sessions" or "68%"
 */
export function getProgressLabel(progress) {
  if (!progress || progress.expectedScores === 0) {
    return 'No Data';
  }

  if (progress.status === 'complete') {
    return 'Complete';
  }

  if (progress.status === 'not_started') {
    return 'Not Started';
  }

  return `${progress.percentage}%`;
}
